import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, Clock, FileSpreadsheet } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';


export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [presentToday, setPresentToday] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', date_of_birth: '', contract_type: 'CDI', full_time: true, hours_per_week: 35, phone: '', salary: 0 });

  const load = () => {
    api.get(`/employees`).then(r => setEmployees(r.data));
    api.get(`/employees/present/today`).then(r => setPresentToday(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openNew = () => { setEditEmp(null); setForm({ first_name: '', last_name: '', email: '', date_of_birth: '', contract_type: 'CDI', full_time: true, hours_per_week: 35, phone: '', salary: 0 }); setShowForm(true); };
  const openEdit = (e) => { setEditEmp(e); setForm({ first_name: e.first_name, last_name: e.last_name, email: '', date_of_birth: e.date_of_birth, contract_type: e.contract_type, full_time: e.full_time, hours_per_week: e.hours_per_week, phone: e.phone, salary: e.salary }); setShowForm(true); };

  const save = async () => {
    try {
      const payload = { ...form, hours_per_week: parseFloat(form.hours_per_week), salary: parseFloat(form.salary) };
      if (editEmp) {
        await api.put(`/employees/${editEmp.id}`, payload);
        toast.success('Employe modifie');
      } else {
        await api.post(`/employees`, payload);
        toast.success('Employe cree');
      }
      setShowForm(false); load();
    } catch { toast.error('Erreur'); }
  };

  const del = async (id) => {
    if (!window.confirm('Archiver cet employe ? Son historique de pointage sera conserve.')) return;
    await api.delete(`/employees/${id}`);
    toast.success('Employe archive'); load();
  };

  const isPresent = (empId) => presentToday.some(p => p.id === empId);

  return (
    <div data-testid="admin-employees-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-[#2A2A28]">Employes</h1>
        <Button onClick={openNew} className="rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="add-employee-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </div>

      {presentToday.length > 0 && (
        <div className="bg-[#5B6B3F]/5 border border-[#5B6B3F]/20 rounded-xl p-4 mb-6">
          <h3 className="text-sm font-medium text-[#5B6B3F] mb-2">Presents aujourd'hui ({presentToday.length})</h3>
          <div className="flex flex-wrap gap-2">
            {presentToday.map(e => <Badge key={e.id} className="bg-[#5B6B3F]/10 text-[#5B6B3F]">{e.first_name} {e.last_name}</Badge>)}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {employees.map(emp => (
          <div key={emp.id} className="bg-white rounded-xl border border-[#EBE8E0] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`employee-${emp.id}`}>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-[#2A2A28]">{emp.first_name} {emp.last_name}</h3>
                <Badge variant="outline" className="text-xs">{emp.contract_type}</Badge>
                {emp.active === false && <Badge className="text-xs bg-[#7A7A75]/10 text-[#7A7A75]">Archive</Badge>}
                {isPresent(emp.id) && <span className="w-2 h-2 rounded-full bg-[#5B6B3F] inline-block" title="Present"></span>}
              </div>
              <p className="text-sm text-[#7A7A75]">{emp.full_time ? 'Temps plein' : 'Temps partiel'} - {emp.hours_per_week}h/semaine</p>
              {emp.phone && <p className="text-xs text-[#7A7A75]">{emp.phone}</p>}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Link to={`/admin/attendance?employee=${emp.id}`}>
                <Button size="sm" variant="outline" className="rounded-lg border-[#EBE8E0]" data-testid={`attendance-${emp.id}`}>
                  <Clock className="w-3.5 h-3.5 mr-1" /> Presence
                </Button>
              </Link>
              <Button size="sm" variant="outline" className="rounded-lg border-[#EBE8E0]" onClick={() => openEdit(emp)} data-testid={`edit-emp-${emp.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" className="rounded-lg border-[#8C3A3A]/20 text-[#8C3A3A]" onClick={() => del(emp.id)} data-testid={`delete-emp-${emp.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {employees.length === 0 && <p className="text-center text-[#7A7A75] py-8">Aucun employe</p>}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editEmp ? 'Modifier' : 'Nouvel employe'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Prenom</Label><Input className="mt-1 rounded-xl border-[#EBE8E0]" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="emp-form-firstname" /></div>
              <div><Label className="text-sm">Nom</Label><Input className="mt-1 rounded-xl border-[#EBE8E0]" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="emp-form-lastname" /></div>
            </div>
            {!editEmp && <div><Label className="text-sm">Email (pour compte employe)</Label><Input type="email" className="mt-1 rounded-xl border-[#EBE8E0]" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="emp-form-email" placeholder="Un compte sera cree automatiquement" /></div>}
            <div><Label className="text-sm">Date de naissance</Label><Input type="date" className="mt-1 rounded-xl border-[#EBE8E0]" value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} /></div>
            <div><Label className="text-sm">Telephone</Label><Input className="mt-1 rounded-xl border-[#EBE8E0]" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Type de contrat</Label>
                <select className="w-full mt-1 h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm" value={form.contract_type} onChange={e => setForm({...form, contract_type: e.target.value})} data-testid="emp-form-contract">
                  <option value="CDI">CDI</option><option value="CDD">CDD</option>
                </select>
              </div>
              <div><Label className="text-sm">Heures/semaine</Label><Input type="number" className="mt-1 rounded-xl border-[#EBE8E0]" value={form.hours_per_week} onChange={e => setForm({...form, hours_per_week: e.target.value})} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.full_time} onCheckedChange={v => setForm({...form, full_time: v})} />
              <Label className="text-sm">Temps plein</Label>
            </div>
            <div><Label className="text-sm">Salaire (EUR)</Label><Input type="number" className="mt-1 rounded-xl border-[#EBE8E0]" value={form.salary} onChange={e => setForm({...form, salary: e.target.value})} /></div>
            <Button onClick={save} className="w-full rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="emp-form-save">{editEmp ? 'Modifier' : 'Creer'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
